import { useQuery } from '@tanstack/react-query';
import { ExternalLink } from 'lucide-react';
import { api } from '../lib/api';

/** "23. jul. 14:33" from an ISO timestamp (already tenant-local from the API). */
function formatAt(at: string | null): string {
  if (!at) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}:\d{2})/.exec(at);
  if (!m) return at;
  const months = ['jan.', 'feb.', 'mar.', 'apr.', 'maj', 'jun.', 'jul.', 'aug.', 'sep.', 'okt.', 'nov.', 'dec.'];
  return `${Number(m[3])}. ${months[Number(m[2]) - 1]} ${m[4]}`;
}

/**
 * The ticket's correspondence as a chat: OUR messages on the right, the customer's on
 * the left, internal notes as centered amber notes. Bodies are plain text from the
 * Messages endpoint and are rendered as text (never HTML). The deep link stays
 * available in every state, including loading and error.
 */
export function TicketThread({ ticketId, href }: { ticketId: number; href?: string }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['ticket-thread', ticketId],
    queryFn: () => api.ticketThread(ticketId),
    staleTime: 60_000,
  });

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <span className="text-xs text-brand-dark/50">
          {data
            ? `${data.messages.length} ${data.messages.length === 1 ? 'besked' : 'beskeder'}${data.truncated ? ' · viser de seneste' : ''}`
            : isLoading
              ? 'Henter korrespondance…'
              : 'Korrespondancen kunne ikke hentes'}
        </span>
        {href && (
          <a href={href} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs font-medium text-brand-accent hover:text-brand-accent-hover">
            Åbn sagen i SuperOffice <ExternalLink size={12} />
          </a>
        )}
      </div>
      {isError ? (
        <p className="py-6 text-center text-sm text-brand-dark/50">Prøv igen, eller åbn sagen direkte i SuperOffice.</p>
      ) : isLoading || !data ? (
        <p className="py-6 text-center text-sm text-brand-dark/50">Henter…</p>
      ) : data.messages.length === 0 ? (
        <p className="py-6 text-center text-sm text-brand-dark/50">Ingen beskeder på sagen.</p>
      ) : (
        <ul className="space-y-3">
          {data.messages.map((m) => (
            <li key={m.id}>
              {m.internal ? (
                <div className="mx-6 rounded-md bg-brand-amber-light/40 px-3 py-2">
                  <div className="text-[10px] font-medium uppercase tracking-wide text-brand-amber">Intern note · {m.author}</div>
                  <p className="mt-0.5 whitespace-pre-wrap break-words text-xs text-brand-dark/70">{m.body}</p>
                </div>
              ) : (
                <div className={`flex ${m.ours ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 ${m.ours ? 'rounded-br-sm bg-brand-primary text-white' : 'rounded-bl-sm bg-brand-primary/5 text-brand-dark'}`}>
                    <div className={`text-[10px] font-medium ${m.ours ? 'text-white/70' : 'text-brand-dark/50'}`}>
                      {m.author}{formatAt(m.at) ? ` · ${formatAt(m.at)}` : ''}
                    </div>
                    <p className="mt-0.5 whitespace-pre-wrap break-words text-sm leading-relaxed">{m.body}</p>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
